<template>
  <a-config-provider
    :theme="{
      token: {
        fontFamily: 'Rethink Sans, Inter',
        colorPrimary: colors.brand03,
        colorTextBase: colors.neutral01,
        colorBgContainer: colors.neutral01,
        colorText: colors.neutral06,
      },
      components: {
        Button: {
          fontSize: '16px',
          fontWeight: 'bold',
          controlHeight: '64px',
          colorTextBase: colors.neutral01,
        },
        Select: {
          fontSize: '16px',
          fontWeight: 'bold',
          controlHeight: '64px',
          colorTextBase: colors.neutral06,
          borderColor: 'transparent',
        },
        Input: {
          fontSize: '16px',
          fontWeight: 'bold',
          outerHeight: '64px',
          colorTextBase: colors.neutral06,
          borderColor: 'none',
        },
      },
    }"
  >
    <SplashScreen v-if="isRenderingSplashScreen && appStore.isApp" />
    <template v-else>
      <div v-if="!isProduction && !isScreenshotMode" class="version">
        {{ info.version }}
        {{ info.build ? `(${info.build}${environment})` : '' }}
      </div>
      <Paywall />
      <ProficiencyLevelChanged v-model="showProficiencyLevelChanged" />
      <FullscreenLoader />
      <main>
        <router-view v-slot="{ Component }" name="header">
          <Transition name="fade" mode="out-in">
            <component :is="Component" :key="nonce" />
          </Transition>
        </router-view>

        <router-view v-slot="{ Component }" class="page-container">
          <Transition :name="transitionName" mode="out-in">
            <component :is="Component" />
          </Transition>
        </router-view>
      </main>

      <router-view v-if="showNavigation" name="navigation"></router-view>
    </template>
  </a-config-provider>
</template>

<script setup lang="ts">
import 'cordova-plugin-purchase';

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen as NativeSplashScreen } from '@capacitor/splash-screen';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import { RouteLocationNormalized, useRoute, useRouter } from 'vue-router';

import FullscreenLoader from '@/components/Loader/Fullscreen.vue';
import Paywall from '@/components/Paywall/index.vue';
import logger from '@/infra/logger';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { usePurchaseStore } from '@/store/purchase';
import { AppPlatform } from '@/types/app';
import { EventName } from '@/types/events';

import packageJSON from '../package.json';
import ProficiencyLevelChanged from './components/ProficiencyLevel/LevelChanged.vue';
import SplashScreen from './components/SplashScreen/SplashScreen.vue';
import getCustomColor from './helpers/get-custom-color';
import initiateInAppPurchases from './helpers/initiate-in-app-purchases';
import isScreenshot from './helpers/is-screenshot';
import { tracking } from './helpers/track-event';
import notificationService from './services/NotificationService';

const router = useRouter();
const route = useRoute();
const transitionName = computed(() => route.meta.transition || 'fade');

const appStore = useAppStore();
const showNavigation = computed(() => appStore.showNavigation);
const clickGoBack = computed(() => appStore.clickGoBack);
const platform = computed(() => appStore.platform);
const viewPortHeight = computed({
  get: () => appStore.viewPortHeight,
  set: (value) => appStore.setViewPortHeight(value),
});
const info = computed(() => appStore.info);
const isStaging = computed(() => appStore.isStaging);
const isProduction = computed(() => appStore.isProduction);
const environment = computed(() => {
  if (isProduction.value) {
    return 'p';
  }

  if (isStaging.value) {
    return 's';
  }

  return 'l';
});

const purchaseStore = usePurchaseStore();
const availableSubscriptions = computed(
  () => purchaseStore.availableSubscriptions,
);

const authStore = useAuthStore();
const token = computed(() => authStore.token);

const colors = computed(() => ({
  neutral01: getCustomColor('--color-neutral-01'),
  neutral06: getCustomColor('--color-neutral-06'),
  brand03: getCustomColor('--color-brand-03'),
}));

const isScreenshotMode = computed(() => isScreenshot());

const nonce = ref(0);
const isEdgeDragNavigating = ref(false);
const routes = ref<RouteLocationNormalized['name'][]>([]);
watch(
  () => route.name,
  (newRouteName, oldRouteName) => {
    if (clickGoBack.value) {
      isEdgeDragNavigating.value = false;
      appStore.setClickGoBack(false);
    }

    if (!oldRouteName) {
      return null;
    }

    nonce.value += 1;
    const lastRoute =
      routes.value.length === 0 ? '' : routes.value[routes.value.length - 1];

    if (newRouteName === lastRoute) {
      routes.value.pop();
    } else {
      routes.value.push(oldRouteName);
    }
  },
);

const isWeb = computed(() => Capacitor.getPlatform() === AppPlatform.Web);
const isIOS = computed(() => Capacitor.getPlatform() === AppPlatform.Ios);

function detectChangeRouteBySwipe() {
  const IOS_EDGE_DRAG_NAVIGATION_THRESHOLD = 25;

  let timer: number | undefined;

  const handleTouchStart = (e: TouchEvent) => {
    if (
      e.touches[0].pageX > IOS_EDGE_DRAG_NAVIGATION_THRESHOLD &&
      e.touches[0].pageX <
        window.innerWidth - IOS_EDGE_DRAG_NAVIGATION_THRESHOLD
    ) {
      return;
    }

    isEdgeDragNavigating.value = true;

    if (timer) {
      clearTimeout(timer);
    }
  };

  const handleTouchEnd = () => {
    timer = setTimeout(() => (isEdgeDragNavigating.value = false), 200);
  };

  window.addEventListener('touchstart', handleTouchStart);
  window.addEventListener('touchend', handleTouchEnd);
}

onBeforeMount(async () => {
  await NativeSplashScreen.hide();

  try {
    if (isIOS.value) {
      detectChangeRouteBySwipe();
    }

    CapacitorApp.addListener('backButton', () => {
      router.back();
    });

    // Ensure that the fullscreen loader is hidden
    appStore.setFullscreenLoading({
      show: false,
      texts: [],
    });

    // Ensure navigation is shown
    appStore.setShowNavigation(true);
  } catch (error) {
    logger.captureException(error);

    return error;
  }
});

async function initFBSDK() {
  try {
    await FacebookLogin.initialize({ appId: '175502742017884' });
    const platform = Capacitor.getPlatform();

    // todo: review here, is that necessary?
    // related issue: https://beconfident.sentry.io/issues/5844622291/events/f07920b58cd74ac9b297d067efac54f4/
    if (platform === 'ios') {
      await FacebookLogin.setAutoLogAppEventsEnabled({ enabled: true });
    }
  } catch (error) {
    logger.captureException(error);

    return error;
  }
}

function appInstallEvent() {
  if (isWeb.value) {
    return;
  }

  const isInstalled = Boolean(localStorage.getItem('isInstalled'));

  if (!isInstalled) {
    tracking.trackEvent(EventName.AppInstalled);
    localStorage.setItem('isInstalled', 'true');
  }
}

const isRenderingSplashScreen = ref(true);
onMounted(async () => {
  setTimeout(() => {
    isRenderingSplashScreen.value = false;
  }, 4500);

  document.addEventListener(
    'deviceready',
    () => {
      const CdvPurchaseStore = new window.CdvPurchase.Store();

      const { Platform, ProductType } = window.CdvPurchase;
      purchaseStore.setCdvPurchaseStore(CdvPurchaseStore);

      if (
        platform.value === AppPlatform.Android ||
        platform.value === AppPlatform.Ios
      ) {
        notificationService.init();
        const hydratedAvailableSubscriptions = [
          ...availableSubscriptions.value,
        ].map((subscription) => ({
          ...subscription,
          type: ProductType.PAID_SUBSCRIPTION,
          platform:
            platform.value === AppPlatform.Android
              ? Platform.GOOGLE_PLAY
              : Platform.APPLE_APPSTORE,
        }));

        purchaseStore.setAvailableSubscriptions(hydratedAvailableSubscriptions);
      }

      appStore.setShowPaywall(false);
      initiateInAppPurchases({
        CdvPurchaseStore,
        router,
        platform: platform.value,
      });
    },
    false,
  );

  initFBSDK();

  if (visualViewport && visualViewport.addEventListener) {
    viewPortHeight.value = `${visualViewport.height}px`;
    visualViewport.addEventListener('resize', () => {
      const visualViewportHeight = visualViewport?.height;
      viewPortHeight.value = `${visualViewportHeight}px`;
    });
  }

  try {
    const platform = Capacitor.getPlatform();
    appStore.setPlatform(appStore.getPlatformMap(platform));

    if (platform === AppPlatform.Web) {
      appStore.setInfo({
        build: packageJSON.buildVersion.toString(),
        version: packageJSON.version,
        package: packageJSON.name,
      });
    } else {
      const appInfo = await CapacitorApp.getInfo();

      if (appInfo) {
        appStore.setInfo({
          build: appInfo.build,
          version: appInfo.version,
          package: appInfo.id,
        });
      }
    }
  } finally {
    appInstallEvent();
    const isAuthenticated = Boolean(token.value);

    if (isAuthenticated) {
      const userData = await authStore.getMe();
      appStore.getStats();
      tracking.onSignIn(userData);
    }
  }
});

const showProficiencyLevelChanged = computed(() =>
  Boolean(
    authStore.lastProficiencyLevel &&
      authStore.user?.proficiencyLevel &&
      authStore.lastProficiencyLevel !== authStore.user?.proficiencyLevel,
  ),
);
</script>

<style>
* {
  font-family: 'Rethink Sans', sans-serif;

  /* Disable text selection */
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.version {
  position: absolute;
  z-index: 100000;
  font-size: 12px;
  color: var(--color-neutral-02);
  top: calc(env(safe-area-inset-bottom) + 2px);
  right: 4px;
}

html,
body {
  overflow: hidden;
  width: 100vw;
  height: 100%;
}

img {
  pointer-events: none;
  -webkit-user-drag: none;
}

.loading-root {
  display: flex;
  align-items: center;
  justify-content: center;
}
#app {
  /* display: grid; */
  /* grid-template-rows: min-content; */
  /* max-height: 100vh; */
  box-sizing: border-box;
  overflow-y: auto;
  height: 100%;
}
.van-overflow-hidden .footer-container {
  z-index: 11;
}
.app-bar-layout {
  width: 100%;
  z-index: 11;
}

@media (min-width: 768px) {
  .page-container {
    max-width: 768px;
    margin: 0 auto;
  }
}

.page-container {
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: 0px !important;
  margin-bottom: env(safe-area-inset-bottom);
  display: flex;
  flex-direction: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.page-container::-webkit-scrollbar {
  display: none;
}

.footer-container {
  z-index: 12;
}

main {
  max-width: 100%;
  grid-template: 'main';
  flex: 1;
  position: relative;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  padding-top: env(safe-area-inset-top);
  height: 100%;
}

main > * {
  grid-area: main;
  background-color: white;
  position: relative;
}

main > :first-child {
  z-index: 12;
}

/* Transitions */

.next-leave-to {
  animation: leaveToLeft 500ms both cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 11;
}

.next-enter-to {
  animation: enterFromRight 500ms both cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 12;
}

.prev-leave-to {
  animation: leaveToRight 500ms both cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 12;
}

.prev-enter-to {
  animation: enterFromLeft 500ms both cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: 11;
}

@keyframes leaveToLeft {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-25%);
  }
}

@keyframes enterFromLeft {
  from {
    transform: translateX(-25%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes leaveToRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

@keyframes enterFromRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.slide-out-leave-active,
.slide-out-enter-active {
  transition: all 0.35s ease-in-out;
}

.slide-out-leave-to {
  opacity: 0;
  transform: translateX(25%);
}

.slide-out-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: all 0.35s ease-in-out;
}

.slide-in-enter-from {
  opacity: 0;
  transform: translateX(0%);
}

.slide-in-enter-to {
  opacity: 1;
}

.slide-in-leave-to {
  opacity: 0.25;
  transform: translateX(-25%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss" scoped></style>
